/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Update Available Modal - Shows when a new build is available
 *
 * Displays different messaging based on update priority:
 * - Critical: Forced update with urgent messaging
 * - Recommended: User can choose to update now or later
 * - Optional: Gentle reminder, easily dismissible
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { RefreshCw, AlertTriangle, Sparkles, X } from 'lucide-react'
import PropTypes from 'prop-types'

// Update priority constants
const UPDATE_PRIORITY = {
  CRITICAL: 'critical',
  RECOMMENDED: 'recommended',
  OPTIONAL: 'optional'
}

/**
 * Sanitize changelog text to prevent XSS
 * Only allows plain text - strips any HTML tags
 */
const sanitizeText = (text) => {
  if (typeof text !== 'string') return ''
  // Remove any HTML tags and trim whitespace
  return text.replace(/<[^>]*>/g, '').trim()
}

const UpdateAvailableModal = ({
  isOpen,
  onUpdate,
  onDismiss = () => {},
  currentBuildId = '',
  newBuildId = '',
  updatePriority = 'recommended',
  changelog = null
}) => {
  const modalRef = useRef(null)
  const updateButtonRef = useRef(null)

  // Determine if this is a forced update (no dismiss option)
  const isForced = updatePriority === UPDATE_PRIORITY.CRITICAL

  // Focus management and keyboard handling
  useEffect(() => {
    if (!isOpen) return

    // Focus the update button when modal opens
    const timer = setTimeout(() => {
      updateButtonRef.current?.focus()
    }, 100)

    // Handle Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isForced && onDismiss) {
        onDismiss()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isForced, onDismiss])

  if (!isOpen) return null

  // Determine severity for styling
  const isCritical = updatePriority === UPDATE_PRIORITY.CRITICAL

  // Get appropriate icon
  const getIcon = () => {
    if (isCritical) return <AlertTriangle className="w-6 h-6 text-white" />
    return <Sparkles className="w-6 h-6 text-white" />
  }

  // Get appropriate title
  const getTitle = () => {
    if (isCritical) return 'Critical Update Required'
    if (updatePriority === UPDATE_PRIORITY.OPTIONAL) return 'Update Available'
    return 'New Version Available'
  }

  // Get appropriate message
  const getMessage = () => {
    if (isCritical) {
      return (
        <>
          <strong>This is a critical update.</strong> To protect your account and data, please
          update immediately. Your work has been saved.
        </>
      )
    }
    if (updatePriority === UPDATE_PRIORITY.OPTIONAL) {
      return (
        <>
          A minor update is available with small improvements. You can update now or continue using
          the current version.
        </>
      )
    }
    return (
      <>
        A new version of the application is available with improvements and bug fixes. We recommend
        updating to get the best experience.
      </>
    )
  }

  // Get header gradient colors
  const getHeaderClasses = () => {
    if (isCritical) {
      return 'bg-gradient-to-r from-red-500 to-red-600'
    }
    return 'bg-gradient-to-r from-primary-500 to-primary-600'
  }

  // Get message box classes
  const getMessageBoxClasses = () => {
    if (isCritical) {
      return 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
    }
    return 'bg-gray-50 dark:bg-gray-700/50'
  }

  // Get message text classes
  const getMessageTextClasses = () => {
    if (isCritical) {
      return 'text-red-800 dark:text-red-200'
    }
    return 'text-gray-600 dark:text-gray-300'
  }

  // Get button classes
  const getButtonClasses = () => {
    if (isCritical) {
      return 'bg-red-500 hover:bg-red-600'
    }
    return 'bg-primary-600 hover:bg-primary-700'
  }

  // Generate screen reader announcement text
  const getAnnouncementText = () => {
    if (isCritical) {
      return `Critical update available. Please update from build ${currentBuildId} to ${newBuildId} immediately.`
    }
    return `New build ${newBuildId} available. You are currently on build ${currentBuildId}.`
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Screen reader announcement */}
          <div role="status" aria-live="assertive" aria-atomic="true" className="sr-only">
            {getAnnouncementText()}
          </div>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={isForced ? undefined : onDismiss}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="update-modal-title"
              aria-describedby="update-modal-description"
              className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${
                isCritical ? 'ring-2 ring-red-500' : ''
              }`}
            >
              {/* Header */}
              <div className={`px-6 py-4 ${getHeaderClasses()}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getIcon()}
                    <h2 id="update-modal-title" className="text-xl font-bold text-white">
                      {getTitle()}
                    </h2>
                  </div>
                  {!isForced && (
                    <button
                      onClick={onDismiss}
                      className="text-white/80 hover:text-white transition-colors"
                      aria-label="Dismiss"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                {/* Build info */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Current
                    </p>
                    <p className="text-sm font-mono font-semibold text-gray-600 dark:text-gray-300">
                      {currentBuildId || 'unknown'}
                    </p>
                  </div>
                  <div className="text-2xl text-gray-400">&rarr;</div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      New
                    </p>
                    <p className="text-sm font-mono font-semibold text-primary-600 dark:text-primary-400">
                      {newBuildId}
                    </p>
                  </div>
                </div>

                {/* Message */}
                <div
                  id="update-modal-description"
                  className={`p-4 rounded-lg mb-4 ${getMessageBoxClasses()}`}
                >
                  <p className={`text-sm ${getMessageTextClasses()}`}>{getMessage()}</p>
                </div>

                {/* Changelog (if provided) - sanitized to prevent XSS */}
                {changelog && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      What's New
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      {Array.isArray(changelog) ? (
                        changelog.map((item, index) => {
                          const sanitized = sanitizeText(item)
                          if (!sanitized) return null
                          return (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-primary-500 mt-1">•</span>
                              <span>{sanitized}</span>
                            </li>
                          )
                        })
                      ) : (
                        <li className="flex items-start gap-2">
                          <span className="text-primary-500 mt-1">•</span>
                          <span>{sanitizeText(changelog)}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex gap-3">
                {!isForced && (
                  <button
                    onClick={onDismiss}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                  >
                    Remind Me Later
                  </button>
                )}
                <button
                  ref={updateButtonRef}
                  onClick={onUpdate}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors ${getButtonClasses()}`}
                >
                  <RefreshCw className="w-4 h-4" />
                  Update Now
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

UpdateAvailableModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDismiss: PropTypes.func,
  currentBuildId: PropTypes.string,
  newBuildId: PropTypes.string,
  updatePriority: PropTypes.oneOf(['critical', 'recommended', 'optional']),
  changelog: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
}

export default UpdateAvailableModal
